import { useEffect, useState } from 'react';
import { LoaderCircle, Trash2 } from 'lucide-react';

import { ApiError } from '@/api/client';
import type { PlanType } from '@/api/types';
import {
  useClearDemo,
  useDeleteTenant,
  useExtendSubscription,
  useSeedDemo,
  useTenantDetail,
  useUpdateTenant,
  useUpdateTenantStatus,
} from '@/api/hooks/useSuperadmin';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from '@/components/ui/use-toast';
import { formatDate, formatDateTime, money } from '@/lib/utils';

const PLANS: PlanType[] = ['Trial', 'Basic', 'Pro', 'Enterprise', 'Custom'];
const STATUS_VARIANT = { Active: 'ok', Suspended: 'low', Cancelled: 'out' } as const;

export function TenantDetailDialog({
  id,
  open,
  onOpenChange,
}: {
  id: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { data, isLoading } = useTenantDetail(id);
  const updateTenant = useUpdateTenant();
  const updateStatus = useUpdateTenantStatus();
  const extend = useExtendSubscription();
  const deleteTenant = useDeleteTenant();
  const seedDemo = useSeedDemo();
  const clearDemo = useClearDemo();

  const [form, setForm] = useState({
    name: '',
    ownerName: '',
    contactPhone: '',
    plan: 'Trial' as PlanType,
    monthlyFee: '',
    setupFee: '',
    setupFeePaid: false,
    maxUsers: '',
    maxMedicines: '',
  });
  const [error, setError] = useState<string | null>(null);
  const [extendMonths, setExtendMonths] = useState('1');
  const [deleteConfirm, setDeleteConfirm] = useState('');
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  useEffect(() => {
    if (data) {
      const p = data.pharmacy;
      setForm({
        name: p.name,
        ownerName: p.ownerName ?? '',
        contactPhone: p.contactPhone ?? '',
        plan: p.plan,
        monthlyFee: String(p.monthlyFee),
        setupFee: String(p.setupFee),
        setupFeePaid: p.setupFeePaid,
        maxUsers: '',
        maxMedicines: '',
      });
      setError(null);
      setDeleteConfirm('');
      setConfirmingDelete(false);
    }
  }, [data]);

  if (!id) return null;

  async function handleSave() {
    if (!id) return;
    setError(null);
    try {
      await updateTenant.mutateAsync({
        id,
        request: {
          name: form.name.trim() || undefined,
          ownerName: form.ownerName.trim() || undefined,
          contactPhone: form.contactPhone.trim() || undefined,
          plan: form.plan,
          monthlyFee: form.monthlyFee ? Number(form.monthlyFee) : undefined,
          setupFee: form.setupFee ? Number(form.setupFee) : undefined,
          setupFeePaid: form.setupFeePaid,
          maxUsers: form.maxUsers ? Number(form.maxUsers) : undefined,
          maxMedicines: form.maxMedicines ? Number(form.maxMedicines) : undefined,
        },
      });
      toast({ title: 'Pharmacy updated', variant: 'success' });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Something went wrong. Please try again.');
    }
  }

  async function handleStatus(status: 'Active' | 'Suspended' | 'Cancelled') {
    if (!id) return;
    try {
      await updateStatus.mutateAsync({ id, request: { status } });
      toast({ title: `Status set to ${status}`, variant: 'success' });
    } catch (err) {
      toast({
        title: 'Could not change status',
        description: err instanceof ApiError ? err.message : undefined,
        variant: 'destructive',
      });
    }
  }

  async function handleExtend() {
    if (!id) return;
    try {
      await extend.mutateAsync({ id, request: { months: Math.max(1, Number(extendMonths) || 1), markSetupPaid: false } });
      toast({ title: `Extended by ${extendMonths} month(s)`, variant: 'success' });
    } catch (err) {
      toast({
        title: 'Could not extend',
        description: err instanceof ApiError ? err.message : undefined,
        variant: 'destructive',
      });
    }
  }

  async function handleDelete() {
    if (!id || !data) return;
    try {
      await deleteTenant.mutateAsync({ id, confirmSlug: deleteConfirm.trim() });
      toast({ title: 'Pharmacy deleted', variant: 'success' });
      onOpenChange(false);
    } catch (err) {
      toast({
        title: 'Could not delete',
        description: err instanceof ApiError ? err.message : undefined,
        variant: 'destructive',
      });
    }
  }

  async function handleSeedDemo() {
    if (!data) return;
    try {
      const result = await seedDemo.mutateAsync(data.pharmacy.slug);
      toast({
        title: result.skipped ? 'Demo data already present' : 'Demo data seeded',
        description: result.skipped ? undefined : `${result.medicines} medicines, ${result.suppliers} suppliers, ${result.customers} customers.`,
        variant: 'success',
      });
    } catch (err) {
      toast({ title: 'Could not seed demo data', description: err instanceof ApiError ? err.message : undefined, variant: 'destructive' });
    }
  }

  async function handleClearDemo() {
    if (!data) return;
    try {
      const result = await clearDemo.mutateAsync(data.pharmacy.slug);
      toast({ title: 'Demo data cleared', description: `${result.medicines} medicines removed.`, variant: 'success' });
    } catch (err) {
      toast({ title: 'Could not clear demo data', description: err instanceof ApiError ? err.message : undefined, variant: 'destructive' });
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{data?.pharmacy.name ?? 'Pharmacy'}</DialogTitle>
          {data && (
            <DialogDescription>
              {data.pharmacy.slug} · {data.pharmacy.ownerEmail}
            </DialogDescription>
          )}
        </DialogHeader>

        {isLoading || !data ? (
          <div className="space-y-3">
            <Skeleton className="h-20" />
            <Skeleton className="h-32" />
          </div>
        ) : (
          <div className="space-y-5">
            <div className="flex flex-wrap items-center gap-2">
              <Badge variant={STATUS_VARIANT[data.pharmacy.status]}>{data.pharmacy.status}</Badge>
              <Badge variant="secondary">{data.pharmacy.plan}</Badge>
              {data.pharmacy.daysRemaining != null && (
                <Badge variant={data.pharmacy.daysRemaining <= 7 ? 'low' : 'outline'}>
                  {data.pharmacy.daysRemaining}d left
                </Badge>
              )}
            </div>

            {/* Usage */}
            <div className="grid grid-cols-3 gap-2 rounded-md bg-secondary/50 p-3 text-center text-xs sm:grid-cols-5">
              <div>
                <p className="tabular text-lg font-semibold">{data.usage.medicines}</p>
                <p className="text-muted-foreground">/ {data.usage.maxMedicines} meds</p>
              </div>
              <div>
                <p className="tabular text-lg font-semibold">{data.users.length}</p>
                <p className="text-muted-foreground">/ {data.usage.maxUsers} users</p>
              </div>
              <div>
                <p className="tabular text-lg font-semibold">{data.usage.sales}</p>
                <p className="text-muted-foreground">sales</p>
              </div>
              <div>
                <p className="tabular text-lg font-semibold">{data.usage.customers}</p>
                <p className="text-muted-foreground">customers</p>
              </div>
              <div>
                <p className="tabular text-lg font-semibold">{data.usage.purchaseOrders}</p>
                <p className="text-muted-foreground">POs</p>
              </div>
            </div>
            {data.usage.lastSaleAt && (
              <p className="-mt-3 text-xs text-muted-foreground">Last sale {formatDateTime(data.usage.lastSaleAt)}</p>
            )}

            {/* Edit form */}
            <div className="space-y-3">
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label htmlFor="t-name">Pharmacy name</Label>
                  <Input id="t-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="t-owner">Owner name</Label>
                  <Input id="t-owner" value={form.ownerName} onChange={(e) => setForm({ ...form, ownerName: e.target.value })} />
                </div>
              </div>

              <div className="grid gap-3 sm:grid-cols-3">
                <div className="space-y-1.5">
                  <Label>Plan</Label>
                  <Select value={form.plan} onValueChange={(v) => setForm({ ...form, plan: v as PlanType })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PLANS.map((p) => (
                        <SelectItem key={p} value={p}>
                          {p}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="t-monthly">Monthly fee (₹)</Label>
                  <Input
                    id="t-monthly"
                    type="number"
                    min={0}
                    value={form.monthlyFee}
                    onChange={(e) => setForm({ ...form, monthlyFee: e.target.value })}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="t-setup">Setup fee (₹)</Label>
                  <Input
                    id="t-setup"
                    type="number"
                    min={0}
                    value={form.setupFee}
                    onChange={(e) => setForm({ ...form, setupFee: e.target.value })}
                  />
                </div>
              </div>

              <div className="grid gap-3 sm:grid-cols-3">
                <div className="space-y-1.5">
                  <Label htmlFor="t-maxusers">Max users (blank = plan default)</Label>
                  <Input
                    id="t-maxusers"
                    type="number"
                    min={1}
                    placeholder={String(data.usage.maxUsers)}
                    value={form.maxUsers}
                    onChange={(e) => setForm({ ...form, maxUsers: e.target.value })}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="t-maxmeds">Max medicines</Label>
                  <Input
                    id="t-maxmeds"
                    type="number"
                    min={1}
                    placeholder={String(data.usage.maxMedicines)}
                    value={form.maxMedicines}
                    onChange={(e) => setForm({ ...form, maxMedicines: e.target.value })}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="t-phone">Contact phone</Label>
                  <Input id="t-phone" value={form.contactPhone} onChange={(e) => setForm({ ...form, contactPhone: e.target.value })} />
                </div>
              </div>

              {error && (
                <p role="alert" className="rounded-md border border-status-expired/30 bg-status-expired/10 px-3 py-2 text-sm text-status-expired">
                  {error}
                </p>
              )}

              <Button onClick={handleSave} disabled={updateTenant.isPending} size="sm">
                {updateTenant.isPending && <LoaderCircle className="animate-spin" />}
                Save changes
              </Button>
            </div>

            {/* Status + extend */}
            <div className="grid gap-3 rounded-md border border-border p-3 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label>Status</Label>
                <div className="flex gap-2">
                  {(['Active', 'Suspended', 'Cancelled'] as const).map((s) => (
                    <Button
                      key={s}
                      size="sm"
                      variant={data.pharmacy.status === s ? 'default' : 'outline'}
                      disabled={updateStatus.isPending || data.pharmacy.status === s}
                      onClick={() => handleStatus(s)}
                    >
                      {s}
                    </Button>
                  ))}
                </div>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="t-extend">Extend subscription (months)</Label>
                <div className="flex gap-2">
                  <Input
                    id="t-extend"
                    type="number"
                    min={1}
                    max={60}
                    value={extendMonths}
                    onChange={(e) => setExtendMonths(e.target.value)}
                    className="w-20"
                  />
                  <Button size="sm" variant="outline" onClick={handleExtend} disabled={extend.isPending}>
                    {extend.isPending && <LoaderCircle className="animate-spin" />}
                    Extend
                  </Button>
                </div>
                {data.pharmacy.subscriptionEndsAt && (
                  <p className="text-xs text-muted-foreground">Currently ends {formatDate(data.pharmacy.subscriptionEndsAt)}</p>
                )}
              </div>
            </div>

            {/* Users */}
            {data.users.length > 0 && (
              <div className="space-y-1.5">
                <Label>Users ({data.users.length})</Label>
                <div className="max-h-32 space-y-1 overflow-y-auto rounded-md border border-border p-2">
                  {data.users.map((u) => (
                    <div key={u.id} className="flex items-center justify-between text-xs">
                      <span className="truncate">
                        {u.name} · {u.email}
                      </span>
                      <span className="shrink-0 text-muted-foreground">
                        {u.role} {!u.isActive && '· inactive'}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Recent claims */}
            {data.recentClaims.length > 0 && (
              <div className="space-y-1.5">
                <Label>Recent claims</Label>
                <div className="space-y-1">
                  {data.recentClaims.slice(0, 5).map((c) => (
                    <div key={c.id} className="flex items-center justify-between rounded-md bg-secondary/50 px-3 py-1.5 text-xs">
                      <span>
                        {money(c.amount)} · {c.method}
                      </span>
                      <Badge variant={c.status === 'Approved' ? 'ok' : c.status === 'Rejected' ? 'out' : 'low'}>
                        {c.status}
                      </Badge>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Demo data */}
            <div className="flex flex-wrap items-center gap-2 rounded-md border border-border p-3">
              <p className="mr-auto text-xs text-muted-foreground">Demo data (onboarding / evaluation)</p>
              <Button size="sm" variant="outline" onClick={handleSeedDemo} disabled={seedDemo.isPending}>
                {seedDemo.isPending && <LoaderCircle className="animate-spin" />}
                Seed demo data
              </Button>
              <Button size="sm" variant="outline" onClick={handleClearDemo} disabled={clearDemo.isPending}>
                {clearDemo.isPending && <LoaderCircle className="animate-spin" />}
                Clear demo data
              </Button>
            </div>

            {/* Delete */}
            <div className="space-y-2 rounded-md border border-status-expired/30 bg-status-expired/5 p-3">
              <p className="text-sm font-medium text-status-expired">Danger zone</p>
              {!confirmingDelete ? (
                <Button size="sm" variant="outline" className="text-status-expired hover:bg-status-expired/10" onClick={() => setConfirmingDelete(true)}>
                  <Trash2 /> Delete pharmacy
                </Button>
              ) : (
                <div className="space-y-2">
                  <p className="text-xs text-muted-foreground">
                    This permanently deletes all data. Type <span className="font-mono">{data.pharmacy.slug}</span> to confirm.
                  </p>
                  <div className="flex gap-2">
                    <Input value={deleteConfirm} onChange={(e) => setDeleteConfirm(e.target.value)} className="max-w-48" />
                    <Button
                      size="sm"
                      variant="destructive"
                      disabled={deleteConfirm.trim() !== data.pharmacy.slug || deleteTenant.isPending}
                      onClick={handleDelete}
                    >
                      {deleteTenant.isPending && <LoaderCircle className="animate-spin" />}
                      Confirm delete
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setConfirmingDelete(false)}>
                      Cancel
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
